import { colors, spacing } from '@procore/core-react'
import { createGlobalStyle } from 'styled-components'
import {
  alignCenterIcon,
  alignLeftIcon,
  alignRightIcon,
  bgColorIcon,
  boldIcon,
  checkmarkIcon,
  cutIcon,
  fontSizeIcon,
  imageIcon,
  indentIcon,
  italicIcon,
  linkIcon,
  orderedListIcon,
  outdentIcon,
  pasteAsTextIcon,
  pasteIcon,
  redoIcon,
  strikeThroughIcon,
  tableIcon,
  textColorIcon,
  underlineIcon,
  undoIcon,
  unorderedListIcon,
} from './icons'

const getIconUrl = (icon: string) => {
  if (icon.startsWith('data:')) {
    return icon
  }
  const encoded = encodeURIComponent(icon)
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29')
    .replace(/'/g, '%27')

  return `data:image/svg+xml;charset=utf-8,${encoded}`
}

export const TextEditorTheme = createGlobalStyle`
  .ck.ck-toolbar .ck-button,
  .ck.ck-toolbar .ck-splitbutton .ck-button,
  .ck.ck-dropdown__panel .ck-button {
    &:hover {
      background-color: ${colors.gray90};
    }

    &:focus-visible {
      outline: 2px solid ${colors.blue40} !important;
      outline-offset: -1px;
      box-shadow: none !important;
      border-color: transparent !important;
    }
  }

  .ck-button:first-child .ck-icon:first-child {
    background-size: 24px;
    background-repeat: no-repeat;
    background-position: center;
  }

  .ck.ck-input {
    --ck-color-input-border: ${colors.gray50};
    --ck-color-input-disabled-border: ${colors.gray50};
  }

  .ck-link-form .ck-button-action {
      font-size: 13px !important;
      line-height: ${spacing.lg} !important;
      background: ${colors.gray90} !important;
      color: ${colors.gray15} !important;
      border-radius: ${spacing.xs} !important;
      font-family: inherit !important;
      font-weight: 600 !important;
      height: ${spacing.xl} !important;
      justify-content: center !important;
      align-items: center !important;
      cursor: pointer !important;
      
      &:hover {
          background: ${colors.gray85} !important;
      }
  }
  
  [data-cke-command="bold"],
  [data-cke-command="italic"],
  [data-cke-command="underline"],
  [data-cke-command="strikethrough"],
  [data-cke-command="alignment:left"],
  [data-cke-command="alignment:center"],
  [data-cke-command="alignment:right"],
  [data-cke-command="bulletedList"],
  [data-cke-command="numberedList"],
  [data-cke-command="outdent"],
  [data-cke-command="indent"],
  [data-cke-command="cut"],
  [data-cke-command="paste"],
  [data-cke-command="pasteAsText"],
  [data-cke-command="fontSize"],
  [data-cke-command="fontColor"],
  [data-cke-command="fontBackgroundColor"],
  [data-cke-command="link"],
  [data-cke-command="insertTable"],
  [data-cke-command="insertImageViaUrl"],
  [data-cke-command="undo"],
  [data-cke-command="redo"] {
    > .ck-icon:first-child > *,
    > .ck-button:first-child > .ck-icon:first-child > *,
    > .ck-splitbutton > .ck-button:first-child > .ck-icon:first-child > * {
      display: none;
    }

    > .ck-icon:first-child,
    > .ck-splitbutton > .ck-button:first-child .ck-icon:first-child,
    > .ck-button > .ck-icon:first-child {
      width: 24px !important;
      height: 24px !important;
    }
  }

  [data-cke-command="bold"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(boldIcon)}');
  }

  [data-cke-command="italic"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(italicIcon)}');
  }

  [data-cke-command="underline"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(underlineIcon)}');
  }

  [data-cke-command="strikethrough"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(strikeThroughIcon)}');
  }

  [data-cke-command="alignment:left"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(alignLeftIcon)}');
  }

  [data-cke-command="alignment:center"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(alignCenterIcon)}');
  }

  [data-cke-command="alignment:right"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(alignRightIcon)}');
  }

  [data-cke-command="bulletedList"] > .ck-splitbutton > .ck-button:first-child .ck-icon:first-child {
    background-image: url('${getIconUrl(unorderedListIcon)}');
  }

  [data-cke-command="numberedList"] > .ck-splitbutton > .ck-button:first-child .ck-icon:first-child {
    background-image: url('${getIconUrl(orderedListIcon)}');
  }

  [data-cke-command="outdent"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(outdentIcon)}');
  }

  [data-cke-command="indent"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(indentIcon)}');
  }

  [data-cke-command="cut"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(cutIcon)}');
  }

  [data-cke-command="paste"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(pasteIcon)}');
  }

  [data-cke-command="pasteAsText"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(pasteAsTextIcon)}');
  }

  [data-cke-command="fontSize"] > .ck-button > .ck-icon:first-child {
    background-image: url('${getIconUrl(fontSizeIcon)}');
    background-size: 19px;
  }

  [data-cke-command="fontColor"] > .ck-button > .ck-icon:first-child {
    background-image: url('${getIconUrl(textColorIcon)}');
  }

  [data-cke-command="fontBackgroundColor"] > .ck-button > .ck-icon:first-child {
    background-image: url('${getIconUrl(bgColorIcon)}');
  }
  [data-cke-command="link"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(linkIcon)}');
  }

  [data-cke-command="insertTable"] > .ck-button > .ck-icon:first-child {
    background-image: url('${getIconUrl(tableIcon)}');
  }

  [data-cke-command="insertImageViaUrl"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(imageIcon)}');
  }

  [data-cke-command="undo"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(undoIcon)}');
  }

  [data-cke-command="redo"] > .ck-icon:first-child {
    background-image: url('${getIconUrl(redoIcon)}');
  }

  [data-cke-command="fontSize"] .ck-dropdown__panel {
    max-width: 130px !important;

    .ck-list__item {
      min-width: 128px !important;
    }

    .ck-list {
      .ck-button {
        padding: 4px 12px !important;
        font-size: 14px !important;
        line-height: 24px !important;

        .ck-button__label {
          font-size: 14px !important;
          font-weight: normal !important;
          font-family: inherit !important;
          line-height: 24px !important;
        }

        &[data-cke-tooltip-text] {
          font-size: 14px !important;

          .ck-button__label {
            font-size: 14px !important;
          }
        }

        &.ck-on {
          position: relative;
          padding-right: 32px !important;

          &::after {
            content: "";
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            width: 24px !important;
            height: 24px !important;
            background-image: url('${getIconUrl(checkmarkIcon)}');
            background-size: 24px 24px;
            background-repeat: no-repeat;
            background-position: center;
            line-height: 1;
          }
        }
      }
    }

    .ck-list-item-button__check-holder {
      display: none !important;
    }
  }
`
